import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState, useCallback, ReactNode } from 'react';

interface TabItem {
	id: string;
	label: string | ReactNode;
	data?: any;
	children?: React.ReactNode;
}

interface SlidingTabsProps {
	tabs: TabItem[];
	children?: (data: any, activeTabId: string) => React.ReactNode;
	defaultActiveTab?: string;
	onChange?: (activeTabId: string) => void;
	className?: string;
	tabBarClassName?: string;
	variant?: 'default' | 'dark';
	/** `switch` shows one panel at a time (better for scrollable panels). */
	panelMode?: 'slide' | 'switch';
}

const HIDDEN_SCROLLBAR_CLASSNAME =
	'[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

const SlidingTabs = ({
	tabs,
	children,
	defaultActiveTab,
	onChange,
	className,
	tabBarClassName,
	variant = 'default',
	panelMode = 'slide',
}: SlidingTabsProps) => {
	const isDark = variant === 'dark';
	const [activeTab, setActiveTab] = useState<string>(
		defaultActiveTab || tabs[0]?.id || '',
	);
	const [indicatorStyle, setIndicatorStyle] = useState<{
		left: number;
		width: number;
	}>({ left: 0, width: 0 });
	const [isInitialized, setIsInitialized] = useState(false);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [hasTabOverflow, setHasTabOverflow] = useState(false);
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const containerRef = useRef<HTMLDivElement>(null);

	const updateIndicator = useCallback((): void => {
		const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);
		const activeTabElement = tabRefs.current[activeIndex];

		if (activeTabElement && containerRef.current) {
			const containerRect = containerRef.current.getBoundingClientRect();
			const tabRect = activeTabElement.getBoundingClientRect();

			setIndicatorStyle({
				left:
					tabRect.left -
					containerRect.left +
					containerRef.current.scrollLeft,
				width: tabRect.width,
			});
		}
	}, [activeTab, tabs]);

	const updateTabCarouselState = useCallback((): void => {
		const container = containerRef.current;
		if (!container) return;

		const maxScrollLeft = container.scrollWidth - container.clientWidth;
		const overflow = maxScrollLeft > 1;

		setHasTabOverflow(overflow);
		setCanScrollLeft(container.scrollLeft > 1);
		setCanScrollRight(container.scrollLeft < maxScrollLeft - 1);
		updateIndicator();
	}, [updateIndicator]);

	useEffect(() => {
		const initializeIndicator = () => {
			if (containerRef.current && tabRefs.current.length > 0) {
				updateTabCarouselState();
				setIsInitialized(true);
			}
		};
		const rafId = requestAnimationFrame(initializeIndicator);
		return () => cancelAnimationFrame(rafId);
	}, [updateTabCarouselState]);

	useEffect(() => {
		if (isInitialized) {
			updateTabCarouselState();
		}
	}, [activeTab, isInitialized, updateTabCarouselState]);

	useEffect(() => {
		if (!isDark || !isInitialized) return;

		const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);
		tabRefs.current[activeIndex]?.scrollIntoView({
			behavior: 'smooth',
			inline: 'nearest',
			block: 'nearest',
		});
	}, [activeTab, isDark, isInitialized, tabs]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const handleResize = () => {
			if (isInitialized) {
				updateTabCarouselState();
			}
		};

		const handleScroll = () => {
			updateTabCarouselState();
		};

		window.addEventListener('resize', handleResize);
		container.addEventListener('scroll', handleScroll, { passive: true });

		const resizeObserver = new ResizeObserver(handleResize);
		resizeObserver.observe(container);

		return () => {
			window.removeEventListener('resize', handleResize);
			container.removeEventListener('scroll', handleScroll);
			resizeObserver.disconnect();
		};
	}, [isInitialized, updateTabCarouselState]);

	const handleTabClick = (tabId: string): void => {
		setActiveTab(tabId);
		onChange?.(tabId);
	};

	const scrollTabs = (direction: -1 | 1) => {
		const container = containerRef.current;
		if (!container) return;

		container.scrollBy({
			left: direction * Math.max(container.clientWidth * 0.55, 120),
			behavior: 'smooth',
		});
	};

	const handleTabStripWheel = (event: React.WheelEvent<HTMLDivElement>) => {
		if (!isDark || !event.ctrlKey) return;

		event.preventDefault();
		containerRef.current?.scrollBy({ left: event.deltaY });
	};

	const showCarouselControls = isDark && hasTabOverflow;

	const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);

	return (
		<div
			className={cn(
				'box-border flex min-h-0 w-full max-w-full flex-col',
				className,
			)}
		>
			<div
				className={cn(
					'mb-3 flex w-full shrink-0 items-stretch border-b',
					isDark ? 'border-slate-700' : 'border-zinc-100',
					tabBarClassName,
				)}
			>
				{showCarouselControls ? (
					<button
						type="button"
						onClick={() => scrollTabs(-1)}
						disabled={!canScrollLeft}
						className={cn(
							'flex w-7 shrink-0 items-center justify-center self-center text-slate-500 transition-colors',
							canScrollLeft
								? 'hover:text-slate-200'
								: 'cursor-not-allowed opacity-25',
						)}
						aria-label="Rolar abas para a esquerda"
					>
						<ChevronLeft className="size-4" strokeWidth={2.25} />
					</button>
				) : null}

				<div
					ref={containerRef}
					onWheel={handleTabStripWheel}
					className={cn(
						'relative flex min-w-0 flex-1 overflow-x-auto overflow-y-hidden',
						isDark && HIDDEN_SCROLLBAR_CLASSNAME,
					)}
				>
					{tabs.map((tab, index) => (
						<button
							key={tab.id}
							ref={(el) => (tabRefs.current[index] = el)}
							onClick={() => handleTabClick(tab.id)}
							data-sliding-tab-id={tab.id}
							className={cn(
								'relative px-3 py-2.5 text-sm font-bold transition-colors duration-200 ease-in-out',
								'flex-shrink-0 whitespace-nowrap focus:outline-none',
								activeTab === tab.id
									? isDark
										? 'text-slate-100'
										: 'text-stone-700'
									: isDark
									  ? 'text-slate-400'
									  : 'text-zinc-400',
							)}
						>
							{tab.label}
						</button>
					))}

					<div
						className={cn(
							'pointer-events-none absolute bottom-0 z-0 h-1 rounded-t-full transition-all duration-300 ease-out',
							isDark ? 'bg-white' : 'bg-stone-700',
							!isInitialized && 'opacity-0',
						)}
						style={{
							left: `${indicatorStyle.left}px`,
							width: `${indicatorStyle.width}px`,
						}}
					/>
				</div>

				{showCarouselControls ? (
					<button
						type="button"
						onClick={() => scrollTabs(1)}
						disabled={!canScrollRight}
						className={cn(
							'flex w-7 shrink-0 items-center justify-center self-center text-slate-500 transition-colors',
							canScrollRight
								? 'hover:text-slate-200'
								: 'cursor-not-allowed opacity-25',
						)}
						aria-label="Rolar abas para a direita"
					>
						<ChevronRight className="size-4" strokeWidth={2.25} />
					</button>
				) : null}
			</div>

			<div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
				{panelMode === 'switch' ? (
					tabs.map((tab) => (
						<div
							key={tab.id}
							data-sliding-panel-id={tab.id}
							data-sliding-panel-active={
								tab.id === activeTab ? 'true' : 'false'
							}
							className={cn(
								'absolute inset-0',
								tab.id !== activeTab && 'hidden',
							)}
						>
							{tab.children
								? tab.children
								: children?.(tab.data, tab.id)}
						</div>
					))
				) : (
					<div
						className="flex h-full min-h-0 transition-transform duration-300 ease-out"
						style={{
							width: `${tabs.length * 100}%`,
							transform: `translateX(-${
								(activeIndex * 100) / tabs.length
							}%)`,
						}}
					>
						{tabs.map((tab) => (
							<div
								key={tab.id}
								data-sliding-panel-id={tab.id}
								data-sliding-panel-active={
									tab.id === activeTab ? 'true' : 'false'
								}
								className="flex h-full min-h-0 min-w-0 shrink-0 flex-col"
								style={{ width: `${100 / tabs.length}%` }}
							>
								<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
									{tab.children
										? tab.children
										: children?.(tab.data, tab.id)}
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export { SlidingTabs };
